/**
 * Windows-1252 code points that differ from ISO-8859-1 (0x80–0x9F).
 * Used to reverse UTF-8 bytes that were misread as Windows-1252 (common API mojibake).
 */
const WINDOWS_1252_REVERSE = new Map<number, number>([
  [0x20ac, 0x80],
  [0x201a, 0x82],
  [0x0192, 0x83],
  [0x201e, 0x84],
  [0x2026, 0x85],
  [0x2020, 0x86],
  [0x2021, 0x87],
  [0x02c6, 0x88],
  [0x2030, 0x89],
  [0x0160, 0x8a],
  [0x2039, 0x8b],
  [0x0152, 0x8c],
  [0x017d, 0x8e],
  [0x2018, 0x91],
  [0x2019, 0x92],
  [0x201c, 0x93],
  [0x201d, 0x94],
  [0x2022, 0x95],
  [0x2013, 0x96],
  [0x2014, 0x97],
  [0x02dc, 0x98],
  [0x2122, 0x99],
  [0x0161, 0x9a],
  [0x203a, 0x9b],
  [0x0153, 0x9c],
  [0x017e, 0x9e],
  [0x0178, 0x9f],
])

function charToWindows1252Byte(code: number): number | null {
  const mapped = WINDOWS_1252_REVERSE.get(code)
  if (mapped !== undefined) {
    return mapped
  }
  if (code <= 0xff) {
    return code
  }
  return null
}

function looksLikeMojibake(value: string): boolean {
  // High-bit Latin-1 or Windows-1252 punctuation left by UTF-8 mis-decoding
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i)
    if ((code >= 0x80 && code <= 0xff) || WINDOWS_1252_REVERSE.has(code)) {
      return true
    }
  }
  return false
}

function decodeUtf8Bytes(bytes: Uint8Array): string {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(bytes)
  }
  // Jest jsdom and some Node contexts lack TextDecoder
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('utf8')
  }
  throw new Error('No UTF-8 decoder available')
}

/**
 * Re-interpret a Windows-1252/Latin-1 mis-decoded string as UTF-8.
 * Returns null when the string is not repairable.
 */
export function repairUtf8Mojibake(value: string): string | null {
  if (!value || !looksLikeMojibake(value)) {
    return null
  }

  const bytes = new Uint8Array(value.length)
  for (let i = 0; i < value.length; i += 1) {
    const mapped = charToWindows1252Byte(value.charCodeAt(i))
    if (mapped === null) {
      return null
    }
    bytes[i] = mapped
  }

  try {
    const repaired = decodeUtf8Bytes(bytes)
    if (!repaired || repaired.includes('\uFFFD') || repaired === value) {
      return null
    }
    // Only accept repairs that produce characters beyond Latin-1 (e.g. Vietnamese).
    let hasUnicode = false
    for (let i = 0; i < repaired.length; i += 1) {
      if (repaired.charCodeAt(i) > 0xff) {
        hasUnicode = true
        break
      }
    }
    if (!hasUnicode) {
      return null
    }
    return repaired
  } catch {
    return null
  }
}

function decodeUriComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '))
  } catch {
    return value
  }
}

/**
 * Fix percent-encoding and UTF-8-as-Windows-1252 mojibake without stripping path segments.
 * Use for resource titles/folder names that may contain `/`.
 */
export function repairFileNameEncoding(value: string): string {
  let decoded = decodeUriComponentSafe(value)
  // Handle accidental double-encoding (UploadModal historically percent-encoded File.name).
  if (/%[0-9A-Fa-f]{2}/.test(decoded)) {
    decoded = decodeUriComponentSafe(decoded)
  }

  const repaired = repairUtf8Mojibake(decoded)
  return (repaired ?? decoded).normalize('NFC')
}

/**
 * Extract a readable file name from a stored name or URL path segment.
 * Fixes percent-encoding and common UTF-8-as-Windows-1252 mojibake (Vietnamese, etc.).
 */
export function getDisplayFileName(nameOrUrl?: string | null, fallback = 'file'): string {
  if (!nameOrUrl) {
    return fallback
  }

  let value = nameOrUrl.trim()
  if (!value) {
    return fallback
  }

  // Prefer the last path segment when a URL/key is passed.
  if (value.includes('/')) {
    try {
      const asUrl = value.includes('://') ? new URL(value) : null
      const path = asUrl?.pathname ?? value
      const segment = path.split('/').filter(Boolean).pop()
      if (segment) {
        value = segment
      }
    } catch {
      const segment = value.split('/').filter(Boolean).pop()
      if (segment) {
        value = segment
      }
    }
  }

  // Strip query/hash if still present on a bare path segment.
  value = value.split('?')[0]?.split('#')[0] ?? value

  return repairFileNameEncoding(value) || fallback
}
